import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { OffsetLimitPaginationResponse } from '~/api/common'
import fetch from '~/common/fetch'
import { FetchOptions } from '~/common/fetchHelpers'
import { queryClient } from '~/common/react-query/queryClient'
import { DateString, PrimaryKey, TimeString } from '~/common/types'
import { Log } from '~/settings/settingsApi'
import { ModuleType } from './jobs'

export const BASE_SHIFT_URL = `/api/v2/shifts`
export const SHIFT_LIST_URL = `${BASE_SHIFT_URL}/list`

enum DaysOfWeek {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY
}

export const DAY_OF_WEEK_OPTIONS = [
  { value: DaysOfWeek.SUNDAY, l10nKey: 'common.days_of_week.abbreviated.sunday' },
  { value: DaysOfWeek.MONDAY, l10nKey: 'common.days_of_week.abbreviated.monday' },
  { value: DaysOfWeek.TUESDAY, l10nKey: 'common.days_of_week.abbreviated.tuesday' },
  { value: DaysOfWeek.WEDNESDAY, l10nKey: 'common.days_of_week.abbreviated.wednesday' },
  { value: DaysOfWeek.THURSDAY, l10nKey: 'common.days_of_week.abbreviated.thursday' },
  { value: DaysOfWeek.FRIDAY, l10nKey: 'common.days_of_week.abbreviated.friday' },
  { value: DaysOfWeek.SATURDAY, l10nKey: 'common.days_of_week.abbreviated.saturday' }
]

export enum ShiftStrategyEnum {
  DEDICATED = 1,
  FLEXIBLE = 2
}

export enum ShiftStatus {
  INACTIVE = 0,
  ACTIVE = 1
}

export enum ShiftDifferentialEnum {
  NONE = -1,
  MULTIPLICATIVE = 0,
  ADDITIVE = 1
}

export interface ShiftFormData {
  name: string
  external_name: string
  start_time: TimeString
  end_time: TimeString
  start_date: DateString
  end_date?: DateString
  applicable_days: DaysOfWeek[]
  status: ShiftStatus
}

export interface ShiftSettingsData {
  enforce_shift_day_validations_on_timesheets: boolean
  enforce_shift_time_validations_on_timesheets: boolean
}

export interface ShiftUserListData {
  id: PrimaryKey
  name: string
  external_name: string
  display_name: string
  start_time: TimeString
  end_time: TimeString
  start_date: DateString
  applicable_days: DaysOfWeek[]
}

export interface ShiftApiData extends ShiftFormData {
  id: PrimaryKey
  job_count: number
  active_work_order_count: number
  is_deletable: boolean
  is_deactivate_allowed: boolean
  display_name: string
}

interface ShiftsConfigResponse {
  can_read: boolean
  can_edit: boolean
  can_read_shift_differential: boolean
}

export const shiftsApi = {
  create: (data: ShiftFormData) => fetch.post(`${BASE_SHIFT_URL}`, data),
  update: (id: number, data: ShiftFormData) => fetch.patch(`${BASE_SHIFT_URL}/${id}`, data),
  delete: (id: number): Promise<void> => fetch.post(`${BASE_SHIFT_URL}/shift_delete/`, [id]),
  list: (queryParams: Record<string, any>): Promise<ShiftApiData[]> =>
    fetch.get(`${BASE_SHIFT_URL}?${qs.stringify(queryParams, { arrayFormat: 'repeat' })}`),
  select: {
    list: (queryParams: Record<string, any>): Promise<ShiftUserListData[]> =>
      fetch.get(`${SHIFT_LIST_URL}${qs.stringify(queryParams, { addQueryPrefix: true })}`)
  },
  get: (id: number): Promise<ShiftApiData> => fetch.get(`${BASE_SHIFT_URL}/${id}`),
  getActivityLogs: (): Promise<OffsetLimitPaginationResponse<Log>> => fetch.get(`${BASE_SHIFT_URL}/logs`),
  getSettingsActivityLogs: (): Promise<OffsetLimitPaginationResponse<Log>> =>
    fetch.get(`${BASE_SHIFT_URL}/settings/logs`),
  reactivate: (id: number) => fetch.post(`${BASE_SHIFT_URL}/activate/`, [id]),
  deactivate: (id: number) => fetch.post(`${BASE_SHIFT_URL}/deactivate/`, [id]),
  getShiftsConfig: (fetchOpts?: FetchOptions): Promise<ShiftsConfigResponse> =>
    fetch.get(`${BASE_SHIFT_URL}/config/`, fetchOpts),
  getDefaultShifts: (queryParams: DefaultShiftsEvaluationParams): Promise<DefaultShiftsResult> =>
    fetch.get(`${BASE_SHIFT_URL}/default/?${qs.stringify(queryParams)}`),
  bulkDeactivate: (ids: number[]) => fetch.post(`${BASE_SHIFT_URL}/deactivate/`, ids),
  bulkActivate: (ids: number[]) => fetch.post(`${BASE_SHIFT_URL}/activate/`, ids),
  bulkDelete: (ids: number[]) => fetch.post(`${BASE_SHIFT_URL}/shift_delete/`, ids),
  shiftRulesVendorList: (): Promise<number[]> => fetch.get('/api/v2/shifts/rules_vendors/')
}

const SHIFTS_KEY = 'shifts'

interface ShiftsListParams {
  search?: string
  ordering?: string
  start_date?: any
  end_date?: any
  start_time?: any
  end_time?: any
  status?: ShiftStatus | string
  id?: string
}

type DefaultShiftsEvaluationParams = {
  work_site_id?: number
  business_unit_id?: number
  job_template_id?: number
  job_category_id?: number
  job_module?: ModuleType
  vendor_id?: number | null
  resource_manager_id?: number | null
  currency_code?: string | null
}

export interface ShiftDifferential {
  shift: number
  shift_name?: string
  rate_differential?: number | null
  type?: ShiftDifferentialEnum | null
}

type DefaultShiftsResult = {
  shift_strategy?: number | null
  shifts?: ShiftApiData[]
  shift_differential_mapping?: ShiftDifferential[]
}

export const useShiftsQuery = (queryParams: ShiftsListParams = {}, disabled = false) => {
  return useQuery({
    queryKey: [SHIFTS_KEY, queryParams],
    queryFn: () => shiftsApi.list(queryParams),
    enabled: !disabled,
    keepPreviousData: true
  })
}

/**
 * Active shifts for use with user select options (instead of mapping url straight into JSFSelect)
 */
export const useShiftsOptionsQuery = () => {
  return useQuery({
    queryKey: [SHIFTS_KEY, 'list'],
    queryFn: () => shiftsApi.select.list({ status: ShiftStatus.ACTIVE }),
    keepPreviousData: true
  })
}

export const useShiftQuery = (id?: number | null) => {
  return useQuery({ queryKey: [SHIFTS_KEY, id], queryFn: () => shiftsApi.get(id!), enabled: Number.isInteger(id) })
}

export const useShiftsConfigQuery = () => {
  return useQuery({ queryKey: [SHIFTS_KEY], queryFn: () => shiftsApi.getShiftsConfig() })
}

export const useCriteriaShifts = (
  queryParams: DefaultShiftsEvaluationParams = {},
  opts?: UseQueryOptions<DefaultShiftsResult>
) => {
  return useQuery({
    queryKey: ['default-shifts-evaluation', queryParams],
    queryFn: () => shiftsApi.getDefaultShifts(queryParams),
    ...opts
  })
}

export const useCreateShiftMutation = (opts?: UseMutationOptions<ShiftFormData, unknown, any>) =>
  useMutation({
    mutationFn: (data: ShiftFormData) => shiftsApi.create(data),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useEditShiftMutation = (opts?: UseMutationOptions<ShiftApiData, unknown, any>) =>
  useMutation({
    mutationFn: ({ id, ...data }) => shiftsApi.update(id, data),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useDeleteShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (id: number) => shiftsApi.delete(id),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useShiftsActivityLogs = () =>
  useInfiniteQuery({
    queryKey: [SHIFTS_KEY, 'activityLogs'],
    // TODO (Tim): Is this doing infinite queries properly?
    queryFn: shiftsApi.getActivityLogs,
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })

export const useDeactivateShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (id: number) => shiftsApi.deactivate(id),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useReactivateShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (id: number) => shiftsApi.reactivate(id),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useBulkDeactivateShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (ids: number[]) => shiftsApi.bulkDeactivate(ids),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useBulkActivateShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (ids: number[]) => shiftsApi.bulkActivate(ids),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useBulkDeleteShiftMutation = (opts?: UseMutationOptions<void, unknown, any>) =>
  useMutation({
    mutationFn: (ids: number[]) => shiftsApi.bulkDelete(ids),
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries([SHIFTS_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

const VENDOR_FROM_SHIFT_RULES_QUERY_KEY = 'vendors_from_shift_rules'
export const useShiftRulesVendorListQuery = () =>
  useQuery({
    queryKey: [VENDOR_FROM_SHIFT_RULES_QUERY_KEY],
    queryFn: () => shiftsApi.shiftRulesVendorList()
  })
